import { FormEvent, useCallback, useState } from 'react'
import { ScanLine } from 'lucide-react'

import { CameraScanner } from '@/components/pos/scan/CameraScanner'
import { Card, CardContent, CardHeader, CardTitle, Input, PosButton, Spinner } from '@/components/ui'
import { useSession } from '@/contexts/SessionContext'
import { AuthModel } from '@/http/models'
import { DeviceModel } from '@/http/models/Device'
import { getDeviceUid } from '@/lib/deviceLink'
import { storage } from '@/lib/storage'
import { configService } from '@/services/ConfigService'

const CODE = /^[A-Za-z0-9]{6}$/

/**
 * Connects this browser to the business (POS.md §12.9.2, O2): in the backoffice, Settings → POS → Connect shows a code
 * as a QR with the text below; scan it here or type it. The business is the one this POS domain belongs to.
 */
export const LinkDevicePage = () => {
  const { completeLink, unlinkedNotice } = useSession()
  const [code, setCode] = useState('')
  const [scanAnchor, setScanAnchor] = useState<DOMRect | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const connect = useCallback(
    async (value: string) => {
      const typed = value.trim().toUpperCase()
      if (!CODE.test(typed)) {
        setError('The code has 6 letters and numbers.')
        return
      }
      setBusy(true)
      setError(null)
      // The link call needs X-System-Uid; a fresh browser may not have fetched it yet.
      if (!configService.getConfig().apiConfig.systemUid) {
        const uid = await AuthModel.getSystemUid()
        if (uid.success && uid.data) {
          storage.write('system_uid', uid.data.system_uid)
          configService.setApiConfig({ systemUid: uid.data.system_uid })
        }
      }
      const res = await DeviceModel.link({
        code: typed,
        device_uid: getDeviceUid(),
        platform: 'web',
        name: 'Web POS',
        device_model: navigator.userAgent.slice(0, 100),
        os: navigator.platform || undefined,
      })
      setBusy(false)
      if (res.success && res.data) {
        completeLink({ device_id: res.data.result._id, linked_at: res.data.result.linked_at, branch_id: res.data.result.branch_id })
      } else {
        setError(res.error?.message ?? 'Could not connect. Check the code and the connection.')
      }
    },
    [completeLink]
  )

  const onScan = useCallback(
    (text: string) => {
      setScanAnchor(null)
      setCode(text.trim().toUpperCase().slice(0, 6))
      connect(text)
    },
    [connect]
  )

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    connect(code)
  }

  return (
    <div className="flex h-dvh items-center justify-center bg-frame p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Connect this device</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {unlinkedNotice && (
            <p className="rounded-md border border-border bg-muted p-3 text-sm text-ink">
              This device was unlinked in the backoffice. Its sales are kept and will sync once it is connected and
              someone signs in again.
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            In the backoffice, open Settings → POS → Connect. Scan the QR code or type the code shown there.
          </p>
            <form onSubmit={onSubmit} className="space-y-3">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={6}
                placeholder="ABC123"
                onCameraScan={onScan}
                autoFocus
                aria-label="Connection code"
                className="text-center tracking-[0.3em]"
              />
              <PosButton type="submit" size="touch" className="w-full" disabled={busy || code.trim().length !== 6}>
                {busy && <Spinner />}
                Connect
              </PosButton>
            </form>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <PosButton
            size="touch"
            variant="secondary"
            className="w-full"
            onClick={(e) => setScanAnchor(scanAnchor ? null : e.currentTarget.getBoundingClientRect())}
            disabled={busy}
          >
            <ScanLine /> Scan QR code
          </PosButton>
          {scanAnchor && <CameraScanner anchor={scanAnchor} onScan={onScan} onClose={() => setScanAnchor(null)} />}
          <a href="/companion" className="block text-center text-sm text-muted-foreground underline">
            Use as a companion of another device
          </a>
        </CardContent>
      </Card>
    </div>
  )
}
